import { defineStore } from 'pinia';
import { useApiStore } from 'src/core/stores/api';
import { useErrorHandling } from 'src/core/composables/useErrorHandling';
import {
  BillingPlanReadType,
  BillingCalculateRequest,
  BillingCalculateResponse,
} from 'src/modules/usage/types/billing';
import { ResponseTablePagination } from 'src/core/types/table_data_type';

type BillingStore = {
  plans: BillingPlanReadType[];
};

/**
 * Represents the billing store.
 */
export const useBillingStore = defineStore('usageBilling', {
  state: () =>
    ({
      plans: [],
    } as BillingStore),

  getters: {
    getPlan:
      (state) =>
      (plan: Partial<BillingPlanReadType>): BillingPlanReadType | undefined => {
        if (plan.name) {
          return state.plans.find((p) => p.name === plan.name);
        } else if (plan.id) {
          return state.plans.find((p) => p.id === plan.id);
        }
        return undefined;
      },
  },

  actions: {
    /**
     * Gets the billing plans.
     * @returns {Promise<boolean>} The status of the request.
     */
    async getBillingPlans(): Promise<{
      status: boolean;
    }> {
      const apiStore = useApiStore();
      const { handleError } = useErrorHandling();
      try {
        const response = await apiStore.service.post<
          ResponseTablePagination<BillingPlanReadType>
        >('/billing/plans/read');
        this.plans = response.data.rows.map((plan) => ({
          ...plan,
          requires_custom_limit: plan.requires_custom_limit ?? false,
        }));
        return {
          status: true,
        };
      } catch (e) {
        handleError('Error getting billing plans', e);
        return {
          status: false,
        };
      }
    },
    /**
     * Calculates consumption limits for a plan based on payload.
     */
    async calculateConsumptionLimits(
      payload: BillingCalculateRequest
    ): Promise<{ status: boolean; data?: BillingCalculateResponse }> {
      const apiStore = useApiStore();
      const { handleError } = useErrorHandling();
      try {
        const { data } = await apiStore.service.post<BillingCalculateResponse>(
          '/billing/plans/calculate',
          payload
        );
        return {
          status: true,
          data,
        };
      } catch (e) {
        handleError('Error calculating consumption limits', e);
        return {
          status: false,
        };
      }
    },
  },
});
